import React, { useEffect, useMemo, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { useDispatch, useSelector } from 'react-redux';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { fetchTimeBalances } from '../../../redux/slices/timeBalanceSlice';
import { getCurrentUser } from '../../../redux/slices/authSlice';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
);

const MONTH_LABELS = [
    'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
    'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'
];

export const buildMonthlyWorkedHoursData = (timeBalances = []) => {
    const monthlyHours = Array(12).fill(0);

    timeBalances.forEach((balance) => {
        if (!balance?.date) return;

        const [year, month] = String(balance.date).split('-').map(Number);
        if (!year || !month || month < 1 || month > 12) return;

        monthlyHours[month - 1] += (balance.worked_minutes || 0) / 60;
    });

    return monthlyHours.map((hours) => Number(hours.toFixed(2)));
};

const BarChart = () => {
    const dispatch = useDispatch();
    const currentUser = useSelector((state) => state.auth.user);
    const [timeBalances, setTimeBalances] = useState([]);

    useEffect(() => {
        if (!currentUser) {
            dispatch(getCurrentUser());
            return;
        }

        if (!currentUser.id) return;

        dispatch(fetchTimeBalances({
            userId: currentUser.id,
            year: new Date().getFullYear(),
        }))
            .unwrap()
            .then(setTimeBalances)
            .catch(() => setTimeBalances([]));
    }, [currentUser, dispatch]);

    const monthlyWorkedHours = useMemo(
        () => buildMonthlyWorkedHoursData(timeBalances),
        [timeBalances]
    );
    
    const data = {
        labels: MONTH_LABELS,
        datasets: [
            {
                data: monthlyWorkedHours,
                // Azul da marca (em hexadecimal para o Chart.js não se queixar)
                backgroundColor: '#87b2e4', 
                // Efeito moderno: barras com cantos redondos!
                borderRadius: 6,
                borderSkipped: false,
                // Escurece ligeiramente quando passas o rato por cima
                hoverBackgroundColor: '#3A70D9',
            },
        ],
    };

    const options = {
        responsive: true,
        // Isto é mágico: permite que o gráfico se estique e encolha perfeitamente dentro do cartão
        maintainAspectRatio: false, 
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#1E0342', // Cor escura elegante
                padding: 12,
                cornerRadius: 8,
                displayColors: false, // Remove aquele quadradinho de cor redundante no tooltip
            }
        },
        scales: {
            y: {
                display: false, // Mantemos o eixo Y escondido para um look mais minimalista
                beginAtZero: true,
            },
            x: {
                ticks: {
                    // Substituímos o 'white' por um cinzento suave
                    color: '#9CA3AF', 
                    font: {
                        weight: '600'
                    }
                },
                grid: {
                    display: false,
                    drawBorder: false, 
                }
            },
        },
    };

    return (
        <div className="atlar-card d-flex flex-column" style={{ height: "100%", padding: "24px 32px" }}>
            

            <div className="mb-4 d-flex justify-content-between align-items-center">
                <h2 className="txt-main fw-bold m-0" style={{ fontSize: "1.1rem" }}>
                    Horas Trabalhadas
                </h2>
                <span 
                    style={{ fontSize: "0.85rem", color: "var(--gray-400)", cursor: "pointer", fontWeight: "600" }}
                >
                    Ver detalhes
                </span>
            </div>
            

            <div className="flex-grow-1" style={{ minHeight: "220px", position: "relative" }}>
                <Bar data={data} options={options} />
            </div>

        </div>
    );
};

export default BarChart;
